import type { JobSeekerProfileStructuredDataSalaryType } from '../../../__generated__/types.gqlgenerated';
import type { GettextInterface } from '../../I18n';
import type Sentiment from '../../util/salary/Sentiment';

import { escapeRegExp } from '../../util/escapeRegExp';
import { getLocaleCurrency } from '../../util/salary/getLocaleCurrency';
import { getPayTypesToLabels } from '../../util/salary/getPayTypesToLabels';
import { parseLocaleCurrencyAmount } from '../../util/salary/parseLocaleCurrencyAmount';

// Helper function to get locale-specific number formatting info
export function getLocaleNumberInfo(locale: string) {
    const formatter = new Intl.NumberFormat(locale);

    // Format a number to detect separators
    const formatted = formatter.format(1234.56);

    // Detect decimal separator by formatting a decimal number
    const decimalFormatted = formatter.format(1.1);
    // The character between 1 and 1 is the decimal separator
    let decimalSeparator = '.';
    if (decimalFormatted.includes(',')) {
        decimalSeparator = ',';
    } else if (decimalFormatted.includes('.')) {
        decimalSeparator = '.';
    }

    // Detect group separator from the formatted number
    let groupSeparator = ',';
    if (formatted.includes(',') && decimalSeparator !== ',') {
        groupSeparator = ',';
    } else if (formatted.includes('.') && decimalSeparator !== '.') {
        groupSeparator = '.';
    } else if (formatted.includes(' ')) {
        groupSeparator = ' ';
    } else if (formatted.includes("'")) {
        groupSeparator = "'";
    } else {
        // No group separator found, use opposite of decimal
        groupSeparator = decimalSeparator === '.' ? ',' : '.';
    }

    return {
        decimalSeparator,
        groupSeparator,
        formatter,
    };
}

// Parse a locale-formatted string to a number using Intl.NumberFormat
export function parseLocaleNumber(value: string, locale: string): number {
    const { decimalSeparator, groupSeparator } = getLocaleNumberInfo(locale);

    // Handle edge cases
    if (!value || value === '') return NaN;

    // Remove group separators and normalize decimal separator
    const normalizedValue = value
        .replace(new RegExp(escapeRegExp(groupSeparator), 'g'), '')
        .replace(decimalSeparator, '.');

    return parseFloat(normalizedValue);
}

// SalaryPreference type for validation
export interface SalaryPreference {
    amount?: string;
    salaryType?: string;
    sentiment?: Sentiment;
}

// Pay bounds by country (can be extended for different countries)
export const payBoundsByCountry: Record<string, Record<string, [number, number]>> = {
    US: {
        ['HOURLY']: [2, 500],
        ['DAILY']: [30, 4000],
        ['WEEKLY']: [40, 20000],
        ['MONTHLY']: [100, 90000],
        ['YEARLY']: [2000, 1000000],
    },
    CA: {
        ['HOURLY']: [2, 500],
        ['DAILY']: [30, 4000],
        ['WEEKLY']: [40, 20000],
        ['MONTHLY']: [100, 90000],
        ['YEARLY']: [2000, 1000000],
    },
    GB: {
        ['HOURLY']: [2, 300],
        ['DAILY']: [30, 2500],
        ['WEEKLY']: [40, 15000],
        ['MONTHLY']: [100, 70000],
        ['YEARLY']: [2000, 800000],
    },
    AU: {
        ['HOURLY']: [2, 400],
        ['DAILY']: [30, 3500],
        ['WEEKLY']: [40, 18000],
        ['MONTHLY']: [100, 80000],
        ['YEARLY']: [2000, 900000],
    },
    // Add more countries as needed
};

// Default pay bounds (US bounds used as default)
export const payBounds = payBoundsByCountry.US;

const localeStringOptions = {
    style: 'decimal',
} as const;

export const validatePayRange = (
    payValue: SalaryPreference,
    locale: string,
    gt: GettextInterface,
) => {
    const amount = parseLocaleCurrencyAmount(payValue.amount || '', locale);
    const type = payValue.salaryType as Exclude<
        JobSeekerProfileStructuredDataSalaryType,
        'BIWEEKLY'
    >;
    const payTypes = getPayTypesToLabels(gt);

    // Extract country from locale
    const country = locale.match(/-([A-Z]{2})$/)?.[1] || 'US';
    const countryPayBounds = payBoundsByCountry[country] || payBounds;

    const [lowerBound, upperBound] = (type && countryPayBounds[type]) || [
        0,
        Number.MAX_SAFE_INTEGER,
    ];

    if (amount && type) {
        const { currencySymbol } = getLocaleCurrency(locale);
        if (amount <= lowerBound) {
            return gt.gettext('Add a pay higher than {0}{1} {2}, or adjust the pay period.', [
                currencySymbol,
                lowerBound.toLocaleString(locale, localeStringOptions),
                payTypes[type],
            ]);
        } else if (amount >= upperBound) {
            return gt.gettext('Add a pay lower than {0}{1} {2}, or adjust the pay period.', [
                currencySymbol,
                upperBound.toLocaleString(locale, localeStringOptions),
                payTypes[type],
            ]);
        }
    }

    return undefined;
};

// Helper function to filter pay input using Intl.NumberFormat
export function filterPayInput(value: string, locale: string): string {
    const { decimalSeparator } = getLocaleNumberInfo(locale);

    // Remove all non-numeric characters except the decimal separator
    const escapedSeparator = escapeRegExp(decimalSeparator);
    const allowedCharsRegex = new RegExp(`[^0-9${escapedSeparator}]`, 'g');
    let filtered = value.replace(allowedCharsRegex, '');

    // Ensure only one decimal separator is allowed
    const separatorIndex = filtered.indexOf(decimalSeparator);
    if (separatorIndex !== -1) {
        // Split at first separator and remove any additional separators from the decimal part
        const beforeSeparator = filtered.substring(0, separatorIndex);
        const afterSeparator = filtered.substring(separatorIndex + 1);

        // Remove any additional separators from the decimal part and limit to 2 digits
        const cleanDecimalPart = afterSeparator
            .replace(new RegExp(escapedSeparator, 'g'), '')
            .substring(0, 2);

        filtered = beforeSeparator + decimalSeparator + cleanDecimalPart;
    }

    // Prevent leading zeros unless it's a decimal (0.xx or 0,xx)
    while (filtered.length > 1 && filtered.startsWith('0') && filtered[1] !== decimalSeparator) {
        filtered = filtered.substring(1);
    }

    return filtered;
}

// Helper to validate if the input string contains only valid pay characters
export function isValidPayInput(value: string, locale: string): boolean {
    // Allow empty input
    if (value === '') return true;

    const { decimalSeparator } = getLocaleNumberInfo(locale);

    const escapedSeparator = escapeRegExp(decimalSeparator);

    // Allow single separator input
    if (value === decimalSeparator) return true;

    // Simple validation: check if it contains only numbers and at most one separator
    const onlyValidChars = new RegExp(`^[0-9${escapedSeparator}]*$`).test(value);
    if (!onlyValidChars) return false;

    // Count separators - should be at most 1
    const valueParts = value.split(decimalSeparator);
    if (valueParts.length > 2) return false; // More than 1 separator means more than 2 parts

    // If there's a separator, check decimal places (max 2)
    if (valueParts.length === 2) {
        // Check if decimal part has more than 2 digits
        if (valueParts[1].length > 2) return false;
    }

    return true;
}
